import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import Icon from '../Shared/Icons';
import { useAuth } from '../../context/AuthContext';
import { getProgram, MEMBER_TYPES, PROGRAMS } from '../../config/programs';
import { BLOCKS, COMMON_FIELDS, RATING_LABELS } from '../../utils/reportSchema';
import { fetchMember, fetchReports, archiveMember } from '../../utils/programData';
import { isoWeekKey, fmtDateTime } from '../../utils/weekUtils';
import { analyseMember } from '../../utils/programAI';
import { ProgramShell, S, T } from './ui';

const ALL_FIELDS = (() => {
  const m = {};
  Object.values(BLOCKS).forEach(b => b.fields.forEach(f => { m[f.key] = f; }));
  COMMON_FIELDS.forEach(f => { m[f.key] = f; });
  return m;
})();

const TREND_KEYS = ['rGrowth', 'rTactics', 'rFocus', 'lsParticipation'];

export default function MemberDetail() {
  const { pid, mid } = useParams();
  const navigate = useNavigate();
  const { userRole } = useAuth();
  const program = getProgram(pid);

  const [member, setMember] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [ai, setAi] = useState('');
  const [aiBusy, setAiBusy] = useState(false);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [mid]);

  async function load() {
    setLoading(true);
    try {
      const [m, rs] = await Promise.all([
        fetchMember(mid),
        fetchReports({ programId: pid, kind: 'individual', memberId: mid }),
      ]);
      setMember(m);
      setReports(rs.sort((a, b) => (b.weekKey || '').localeCompare(a.weekKey || '')));
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  async function runAI() {
    setAiBusy(true);
    try { setAi(await analyseMember(member, reports, pid)); }
    catch (e) { console.error(e); setAi('AI haipatikani kwa sasa. Jaribu tena baadaye.'); }
    setAiBusy(false);
  }

  async function archive() {
    if (!window.confirm('Ondoa mwanachama huyu kwenye orodha? Ripoti zake zitabaki.')) return;
    await archiveMember(mid);
    navigate(`/programs/${pid}/members`);
  }

  if (!program) return <Navigate to="/programs" />;
  if (loading) return <ProgramShell program={program} tab="members"><div style={{ ...S.card, ...S.empty }}>Inapakia...</div></ProgramShell>;
  if (!member) return <ProgramShell program={program} tab="members"><div style={{ ...S.card, ...S.empty }}>Mwanachama hakupatikana.</div></ProgramShell>;

  const thisWeek = reports.find(r => r.weekKey === isoWeekKey());

  // Mwenendo wa alama za wiki 8 za mwisho
  const trend = reports.slice(0, 8).reverse();

  return (
    <ProgramShell program={program} tab="members">

      <div style={{ ...S.card, borderLeft: `4px solid ${program.color}` }}>
        <div style={{ display: 'flex', gap: '13px', alignItems: 'center' }}>
          <div style={{
            width: '52px', height: '52px', borderRadius: '15px', flexShrink: 0,
            background: `${program.color}15`, display: 'flex', alignItems: 'center',
            justifyContent: 'center', fontSize: '20px', fontWeight: 800, color: program.color,
          }}>
            {(member.fullName || '?').charAt(0).toUpperCase()}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: T.serif, fontSize: '20px', color: T.green }}>{member.fullName}</div>
            <div style={{ fontSize: '12px', color: T.muted, marginTop: '4px' }}>
              {MEMBER_TYPES[member.type]?.label}
              {member.gender ? ` · ${member.gender}` : ''}
              {member.className ? ` · Darasa ${member.className}` : ''}
            </div>
          </div>
        </div>

        <div style={{ marginTop: '15px', paddingTop: '14px', borderTop: `1px solid #F3F1EB`, fontSize: '12.5px', color: T.muted, lineHeight: 1.9 }}>
          {member.school && <div><strong style={{ color: T.ink }}>Shule:</strong> {member.school}</div>}
          {member.centre && <div><strong style={{ color: T.ink }}>Kituo:</strong> {member.centre}</div>}
          {member.area && <div><strong style={{ color: T.ink }}>Eneo:</strong> {member.area}</div>}
          {member.clubName && <div><strong style={{ color: T.ink }}>Club:</strong> {member.clubName}</div>}
          {member.chessLevel && <div><strong style={{ color: T.ink }}>Ngazi ya chess:</strong> {member.chessLevel}</div>}
          {member.phone && <div><strong style={{ color: T.ink }}>Simu:</strong> {member.phone}</div>}
          {member.parentName && <div><strong style={{ color: T.ink }}>Mzazi:</strong> {member.parentName} {member.parentPhone || ''}</div>}
          {member.type === 'mzazi' && (
            <>
              <div><strong style={{ color: T.ink }}>Mtaji:</strong> {member.hasCapital ? `Amepewa${member.businessName ? ` · ${member.businessName}` : ''}` : 'Hajapewa bado'}</div>
              <div><strong style={{ color: T.ink }}>Chess:</strong> {member.learnsChess ? 'Anajifunza' : 'Hajifunzi (hiari)'}</div>
            </>
          )}
        </div>

        <div style={{ display: 'flex', gap: '6px', marginTop: '13px', flexWrap: 'wrap' }}>
          {(member.programIds || []).map(id => {
            const p = PROGRAMS.find(x => x.id === id);
            if (!p) return null;
            return <span key={id} style={{ ...S.pill, color: p.color, background: `${p.color}15` }}>{p.short}</span>;
          })}
        </div>

        <div style={{ display: 'flex', gap: '9px', marginTop: '16px', flexWrap: 'wrap' }}>
          <button style={{ ...S.btn, background: program.color, flex: '1 1 160px' }}
            onClick={() => navigate(`/programs/${pid}/report/${mid}`)}>
            {thisWeek ? 'Fungua ripoti ya wiki hii' : 'Andika ripoti ya wiki hii'}
          </button>
          <button style={S.btnGhost} onClick={() => navigate(`/programs/${pid}/members/${mid}/edit`)}>Hariri</button>
          {pid === 'msaada' && member.type === 'mtoto' && (
            <button style={S.btnGhost} onClick={() => navigate(`/programs/${pid}/members/${mid}/baseline`)}>
              📋 Breaking the Cycle
            </button>
          )}
          {userRole === 'admin' && <button style={{ ...S.btnGhost, color: '#DC2626' }} onClick={archive}>Ondoa</button>}
        </div>
      </div>

      {/* Mwenendo */}
      {trend.length >= 2 && (
        <div style={S.card}>
          <h2 style={S.h2}>Mwenendo (wiki {trend.length} za mwisho)</h2>
          {TREND_KEYS.map(k => {
            const f = ALL_FIELDS[k];
            if (!f) return null;
            const pts = trend.map(r => Number(r.values?.[k]) || 0);
            if (!pts.some(p => p > 0)) return null;
            return (
              <div key={k} style={{ marginBottom: '15px' }}>
                <div style={{ fontSize: '12px', color: T.muted, fontWeight: 700, marginBottom: '7px' }}>{f.label}</div>
                <div style={{ display: 'flex', gap: '4px', alignItems: 'flex-end', height: '46px' }}>
                  {pts.map((v, i) => (
                    <div key={i} style={{ flex: 1, textAlign: 'center' }}>
                      <div style={{
                        height: `${(v / 5) * 40}px`, background: v >= 4 ? '#059669' : v >= 3 ? program.color : '#D97706',
                        borderRadius: '4px 4px 0 0', minHeight: v ? '3px' : '0',
                      }} />
                      <div style={{ fontSize: '9px', color: T.muted, marginTop: '3px' }}>{v || '–'}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* AI */}
      <div style={S.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '11px', gap: '10px' }}>
          <div>
            <h2 style={{ ...S.h2, marginBottom: '2px' }}>Uchambuzi wa AI</h2>
            <div style={{ fontSize: '11.5px', color: T.muted }}>Kutokana na ripoti {reports.length} zilizoandikwa</div>
          </div>
          <button style={{ ...S.btn, background: T.orange, padding: '10px 16px', opacity: aiBusy ? 0.6 : 1 }}
            disabled={aiBusy || !reports.length} onClick={runAI}>
            {aiBusy ? 'Inafikiri...' : 'Chambua'}
          </button>
        </div>
        {ai ? (
          <div style={{
            fontSize: '13.5px', color: T.ink, lineHeight: 1.8, whiteSpace: 'pre-wrap',
            background: '#FAF8F3', padding: '15px', borderRadius: '12px',
          }}>{ai}</div>
        ) : (
          <div style={{ fontSize: '12.5px', color: T.muted, lineHeight: 1.6 }}>
            {reports.length
              ? 'Bofya "Chambua" ili AI isome ripoti zake zote na kukupa mwenendo, nguvu, udhaifu na ushauri.'
              : 'Andika ripoti chache kwanza kisha AI itaweza kuchambua.'}
          </div>
        )}
      </div>

      {/* Historia ya ripoti */}
      <div style={S.card}>
        <h2 style={S.h2}>Ripoti Zake ({reports.length})</h2>
        {reports.length === 0 ? (
          <div style={S.empty}>Bado hana ripoti yoyote.</div>
        ) : reports.map(r => {
          const isOpen = openId === r.id;
          return (
            <div key={r.id} style={{ borderTop: `1px solid #F3F1EB` }}>
              <div onClick={() => setOpenId(isOpen ? null : r.id)}
                style={{ padding: '12px 0', cursor: 'pointer', display: 'flex', gap: '10px', alignItems: 'center' }}>
                <Icon name="report" size={15} color={program.color} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: T.ink }}>{r.weekLabel || r.weekKey}</div>
                  <div style={{ fontSize: '11px', color: T.muted, marginTop: '2px' }}>
                    Na {r.authorName}
                    {r.contributions?.length ? ` · nyongeza ${r.contributions.length}` : ''}
                  </div>
                </div>
                <span style={{ color: T.muted, transform: isOpen ? 'rotate(90deg)' : 'none' }}>›</span>
              </div>
              {isOpen && (
                <div style={{ paddingBottom: '16px' }}>
                  {Object.entries(r.values || {}).filter(([, v]) => v !== '' && v !== null && v !== undefined).map(([k, v]) => {
                    const f = ALL_FIELDS[k];
                    if (!f) return null;
                    return (
                      <div key={k} style={{ marginBottom: '11px' }}>
                        <div style={{ fontSize: '11px', color: T.muted, fontWeight: 700 }}>{f.label}</div>
                        <div style={{ fontSize: '13px', color: T.ink, marginTop: '2px', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                          {f.type === 'rating' ? `${v}/5 — ${RATING_LABELS[v]}` : String(v)}
                        </div>
                      </div>
                    );
                  })}
                  {(r.contributions || []).map((c, i) => (
                    <div key={i} style={{ background: '#FAF8F3', padding: '11px', borderRadius: '10px', marginTop: '9px' }}>
                      <div style={{ fontSize: '11px', fontWeight: 700, color: program.color }}>Nyongeza — {c.byName}</div>
                      <div style={{ fontSize: '12.5px', color: T.ink, marginTop: '3px', lineHeight: 1.6 }}>{c.text}</div>
                    </div>
                  ))}
                  <div style={{ fontSize: '10.5px', color: T.muted, marginTop: '10px' }}>
                    {fmtDateTime(r.createdAt)}
                  </div>
                  <button style={{ ...S.btnGhost, padding: '7px 13px', fontSize: '11.5px', marginTop: '10px' }}
                    onClick={() => navigate(`/programs/${pid}/report/${mid}?week=${r.weekKey}`)}>
                    Fungua ripoti
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

    </ProgramShell>
  );
}
